import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRegistrationFlow } from '../context/RegistrationFlowContext';
import { ROUTES, NAVIGATION_OPTIONS } from '../config/routes';
import { convertToDisplayName, isBankTransferPayment } from '../utils/paymentMethodUtils';
import { styles } from '../styles/components/RegistrationCompletePageStyles';
import BankTransferInfo from '../components/ui/BankTransferInfo';
import { useNavigationBlock } from '../hooks/useNavigationBlock';
import NavigationBlockModal from '../components/ui/NavigationBlockModal';

/**
 * 会員登録完了画面コンポーネント（統合版）
 * 口座振替の場合は詳細ステップを表示、クレジットカードの場合はシンプル表示
 */
const RegistrationCompletePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { getAllRegistrationData } = useRegistrationFlow();

  // ナビゲーションブロックの状態管理
  const [isNavigationAllowed, setIsNavigationAllowed] = useState(false);
  const [isBlockerReady, setIsBlockerReady] = useState(false);

  // ブロック条件: 「サービスを開始する」ボタンを押すまでブロック
  const shouldBlockNavigation = isBlockerReady && !isNavigationAllowed;

  const { isModalOpen, handleConfirm, handleCancel } = useNavigationBlock(
    shouldBlockNavigation,
    null,
    ROUTES.LOGIN
  );

  // コンポーネントマウント後、少し遅延させてブロッカーを有効化
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsBlockerReady(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);
  
  // React Routerのstateからデータを取得
  const { message, memberData } = location.state || {};

  // URLパラメータから会員IDを取得
  const urlParams = new URLSearchParams(window.location.search);
  const memberId = urlParams.get('memberId');

  // history.stateからもデータを取得（RegisterSuccessModalからの遷移用）
  const historyState = window.history.state || {};
  const finalMemberData = memberData || historyState.memberData;
  const finalMessage = message || historyState.message;

  // 登録フローデータから支払い方法を取得
  const registrationData = getAllRegistrationData();
  const paymentMethod = registrationData?.paymentMethod;
  const isBankTransfer = isBankTransferPayment(paymentMethod);

  // 店舗名を取得
  const storeName = 
    registrationData?.memberInfo?.storeName || 
    registrationData?.memberInfo?.shop || 
    finalMemberData?.storeName || 
    finalMemberData?.shop;    

  // 会員情報から名前を取得
  const memberName = finalMemberData?.name || registrationData?.memberInfo?.name;

  /**
   * ログアウト処理
   */
  const handleLogout = async () => {
    try {
      // ナビゲーションを許可
      setIsNavigationAllowed(true);

      if (logout) {
        await logout();
        navigate(ROUTES.LOGIN, NAVIGATION_OPTIONS.REPLACE);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  /**
   * 会員情報アイテムのレンダリング
   * @param {string} label - ラベル
   * @param {string} value - 値
   * @returns {JSX.Element|null} JSX要素またはnull
   */
  const renderMemberInfoItem = (label, value) => {
    if (!value) return null;
    
    return (
      <p style={styles.memberInfoItem} key={label}>
        <span style={styles.memberInfoLabel}>{`${label}:`}</span>
        <span style={styles.memberInfoValue} translate="no">{value}</span>
      </p>
    );
  };

  /**
   * ホームボタンのマウスイベントハンドラー
   */
  const handleMouseEnter = (e) => {
    Object.assign(e.target.style, styles.homeButtonHover);
  };

  const handleMouseLeave = (e) => {
    e.target.style.backgroundColor = styles.homeButton.backgroundColor;
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = styles.homeButton.boxShadow;
  };

  return (
    <>
      <div style={styles.container}>
        {/* ページタイトル */}
        <h1 style={styles.title}>会員登録完了</h1>
        
        {/* 成功メッセージ表示（propsで渡された場合） */}
        {finalMessage && (
          <div style={styles.messageBox}>
            {finalMessage}
          </div>
        )}

        {/* 完了通知テキスト */}
        <div>
          <p style={styles.completionText}>
            ご登録いただいたメールアドレスに確認メールをお送りしました。
          </p>
          <p style={styles.completionText}>
            店舗でのご利用方法については、マイページからご確認ください。
          </p>
        </div>

        {/* 登録情報確認エリア */}
        {(finalMemberData || memberId) && (
          <div style={styles.memberInfoContainer}>
            <h3 style={styles.memberInfoTitle}>登録情報</h3>
            <div style={styles.memberInfoBox}>
              {memberId && renderMemberInfoItem('会員ID', memberId)}
              {paymentMethod && renderMemberInfoItem('支払い方法', convertToDisplayName(paymentMethod))}
            </div>
          </div>
        )}

        {/* 口座振替の場合の支払い情報表示 */}
        {isBankTransfer && (
          <div style={styles.paymentInfoSection}>
            <h2 style={styles.paymentInfoTitle}>お支払い・口座振替手続き</h2>
            
            {/* 口座振替選択の案内 */}
            <div style={styles.bankTransferNotice}>
              <div style={styles.noticeTitle}>
                口座振替をお選びいただきました
              </div>
              <div style={styles.noticeText}>
                <span>初期費用のお振込先と月額会費の口座振替手続きについてご案内いたします。</span><br/>
                <span>{`${memberName ? `${memberName}様、` : ''}以下の2つのステップで手続きを完了してください。`}</span>
              </div>
            </div>
          
          {/* BankTransferInfoコンポーネントを使用（大幅に簡略化） */}
          <BankTransferInfo 
            showTitle={false}
            storeName={storeName}
            showAmount={true}
            containerStyle={{
              boxShadow: 'none',
              border: 'none',
              padding: 0,
              backgroundColor: 'transparent'
            }}
          />
          </div>
        )}

        {/* アクションボタンエリア */}
        <div style={styles.actionContainer}>
          <button 
            onClick={handleLogout}
            style={styles.homeButton}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            サービスを開始する
          </button>
        </div>
      </div>

      {/* ナビゲーションブロックモーダル */}
      <NavigationBlockModal
        isOpen={isModalOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default RegistrationCompletePage;